import { Directive, HostListener } from '@angular/core';

/**
 * Directiva que restringe la entrada de un input para permitir solo números (0-9)
 *
 * Uso:
 * <input type="tel" numbersOnly [(ngModel)]="phoneNumber">
 *
 * Permite:
 * - Números del 0 al 9
 * - Backspace, Delete, Tab, Escape, Enter
 * - Teclas de navegación (Home, End, flechas)
 * - Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X (copiar/pegar/cortar)
 *
 * Bloquea:
 * - Letras
 * - Símbolos especiales
 * - Espacios
 */
@Directive({
  selector: '[numbersOnly]'
})
export class NumbersOnlyDirective {

  /**
   * Escucha el evento keydown del input
   * @param event - Evento del teclado
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Lista de teclas permitidas (códigos de tecla)
    const allowedKeys = [
      'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Delete', 'Escape', 'Enter'
    ];

    // Permitir teclas de navegación y control
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Cmd+A, Cmd+C, Cmd+V, Cmd+X
    if ((event.ctrlKey || event.metaKey) &&
        ['a', 'c', 'v', 'x', 'A', 'C', 'V', 'X'].includes(event.key)) {
      return;
    }

    // Si no es un número (0-9), prevenir la entrada
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Escucha el evento paste para limpiar caracteres no numéricos
   * @param event - Evento del portapapeles
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedInput = event.clipboardData?.getData('text/plain');

    if (pastedInput) {
      // Filtrar solo números del texto pegado
      const filteredInput = pastedInput.replace(/\D/g, '');

      // Insertar el texto filtrado en la posición del cursor
      const input = event.target as HTMLInputElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;

      const newValue = currentValue.substring(0, start) + filteredInput + currentValue.substring(end);
      input.value = newValue;

      // Posicionar el cursor después del texto pegado
      const newPosition = start + filteredInput.length;
      input.setSelectionRange(newPosition, newPosition);

      // Disparar evento input para actualizar ngModel
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Escucha el evento drop para limpiar caracteres no numéricos al arrastrar texto
   * @param event - Evento de drag and drop
   */
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const droppedInput = event.dataTransfer?.getData('text');

    if (droppedInput) {
      // Filtrar solo números del texto arrastrado
      const filteredInput = droppedInput.replace(/\D/g, '');

      const input = event.target as HTMLInputElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;

      const newValue = currentValue.substring(0, start) + filteredInput + currentValue.substring(end);
      input.value = newValue;

      const newPosition = start + filteredInput.length;
      input.setSelectionRange(newPosition, newPosition);

      // Disparar evento input para actualizar ngModel
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
